/** Empty-state placeholder strings rendered by read-only / value-display field primitives when the underlying value is
 * missing. A brand-audit copy change lands here instead of at every callsite, where capitalisation drift
 * (`'Not Specified'` / `'Unspecified'`) is the typical follow-on bug. A runtime forbid-rule built from
 * `Object.values()` at module load is the canonical complement.
 */

/** Placeholder strings keyed by the empty-state copy decision they represent.
 * `NOT_SPECIFIED` -- default placeholder when an optional field has no value.
 * `NO_ADDRESS` -- property-address-missing placeholder when a record's address columns are all empty. ONE decision at
 * every callsite, which is why it folds to a single key.
 * `UNKNOWN` -- unresolvable-value placeholder where a value is expected to exist but could not be resolved (a display
 * name that fell through every candidate column, a timestamp that would not parse, a status/type table cell whose
 * column is null). THIS member is the single home for the unresolvable-value placeholder: the grab-bag leaf that
 * carried a byte-identical restatement of it no longer exists, so an importer reaching for that family has exactly one
 * record to reach for. The `'Unknown'` here is the user-facing capitalized form and is distinct from the
 * lowercase `'unknown'` diagnostic sentinel written to logs.
 * `NOT_DISCLOSED` -- WITHHELD, not missing: the value exists and the seller has chosen not to publish it (an undisclosed
 * auction reserve, an undisclosed clear-title flag, an undisclosed maximum security deposit). A buyer reads this as a
 * fact about the listing, which is why it does not fold into `UNKNOWN`.
 * The latter three are deliberately NOT merged with `NOT_SPECIFIED`: they answer different questions, and a brand-audit
 * copy change would plausibly move one and not the other.
 */
export const EMPTY_STATE_PLACEHOLDERS = {
	NOT_SPECIFIED: 'Not specified',
	NO_ADDRESS: 'No address',
	UNKNOWN: 'Unknown',
	NOT_DISCLOSED: 'Not disclosed',
} as const;
